import { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { captureException, captureMessage } from "@sentry/react";

import {
  emailValidationRules,
  Form,
  FormInput,
  FormSubmit,
} from "@components/forms";

import { updateCustomerEmail } from "@lib/api/customerApi";
import {
  classifyUpdateEmailError,
  isUpdateEmailSessionFailure,
} from "@lib/api/updateEmailError";
import { useAuthentication } from "@lib/authentication";
import { AuthenticationErrors } from "@lib/authentication/amplify/identity-provider.enum";

import { CHECKOUT_INPUT_CLASS } from "./checkoutForm";

const normalize = (email: string) => email.trim().toLowerCase();

type SetPasswordForm = {
  email: string;
  confirmEmail: string;
  newPassword: string;
};

// U5/U6 — payment-first post-charge account setup. Rendered inline within the
// checkout flow (never a standalone route) so the in-memory dummy credential
// from U3 stays in the checkout component tree and the authenticated session
// created at post-charge sign-in is still live for updatePassword +
// updateCustomerEmail. Kept self-contained so it deletes cleanly when the A/B
// test concludes.
export const VariantSetPassword = ({
  email,
  dummyPassword,
  onComplete,
}: {
  email: string;
  dummyPassword: string;
  onComplete: () => void | Promise<void>;
}) => {
  const { t } = useTranslation();
  // mode "onTouched" (validate on first blur, then per keystroke) so inline
  // errors don't flash while a field is being typed into for the first time,
  // while formState.isValid still gates the CTA live (OMG-2100/OMG-2110;
  // adapted from the RedeemForm precedent).
  const { register, handleSubmit, getValues, formState } =
    useForm<SetPasswordForm>({
      mode: "onTouched",
      defaultValues: { email },
    });
  const { updatePassword } = useAuthentication();
  const [errors, setErrors] = useState<Record<string, any>>({});
  // A field's rendered error is the server-side one (email API / Cognito,
  // held in local state) or the live react-hook-form validation error —
  // touched-field validation renders through formState directly because the
  // disabled-until-valid CTA makes a failed *submit* unreachable for empty
  // fields (OMG-2110: errors surface on touch/blur, not a submit click).
  const fieldError = (f: keyof SetPasswordForm) =>
    errors[f] ?? formState.errors[f];
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Set once updatePassword has thrown for a non-recoverable reason (session
  // lapse), so the paid user always has an escape hatch — a session lapse (vs a
  // weak password) cannot be fixed by retrying here, and R13 requires never
  // stranding them.
  const [updateFailed, setUpdateFailed] = useState(false);
  // Password-step idempotency: after the dummy→real swap succeeds the dummy is
  // no longer valid, so a resubmit (e.g. after a later onComplete hiccup) must
  // not re-run updatePassword. The email step is made idempotent by
  // accountEmailRef instead (an unchanged value is skipped; a NEW correction
  // after a prior success still runs).
  const passwordSetRef = useRef(false);
  // The email the account currently resolves under (used for the recovery link
  // and to short-circuit the correction call when unchanged). Updated once a
  // correction succeeds so a subsequent password failure recovers to the right
  // inbox — and so a further edit is detected as a new correction to apply.
  const accountEmailRef = useRef(normalize(email));

  // Map an email-correction rejection to user copy (OMG-2111: differentiated —
  // the single vague fallback previously swallowed session lapses, timeouts,
  // and purpose-written backend messages alike). Classification is shared with
  // the member menu's Change Email panel (updateEmailError.ts); the copy here
  // stays checkout-specific — a session lapse points at the reset-link escape
  // below, which only this screen renders.
  const emailErrorMessage = (e: unknown): string => {
    const classified = classifyUpdateEmailError(e);
    switch (classified.kind) {
      case "session":
        return t("setPassword.sessionExpired", {
          defaultValue:
            "Your session has expired — use the reset link below to finish setting up your account.",
        });
      case "network":
        return t("setPassword.connectionError", {
          defaultValue:
            "We couldn't reach the server. Check your connection and try again.",
        });
      case "emailInUse":
        return t("setPassword.emailInUse", {
          defaultValue:
            "That email is already associated with another account.",
        });
      case "invalidEmail":
        return t("js.checkout.emailInvalid", {
          defaultValue: "Please enter a valid email address.",
        });
      case "backendMessage":
        return classified.message;
      default:
        return t("setPassword.emailUpdateFailed", {
          defaultValue: "We couldn't update your email. Please try again.",
        });
    }
  };

  const onSubmit = async (data: Record<string, string>) => {
    setIsSubmitting(true);
    try {
      // U6: correct the account email first (before the password swap) so a
      // rejection leaves the dummy credential intact and the form cleanly
      // resubmittable. The post-charge session authorizes it. Idempotent on
      // accountEmailRef — an unchanged value is skipped, a further edit re-runs.
      // Its OWN try so an email-step rejection is classified as an email error
      // and never misattributed to the password swap (which hasn't run yet).
      const nextEmail = normalize(data.email);
      if (nextEmail !== accountEmailRef.current) {
        try {
          await updateCustomerEmail(nextEmail);
          accountEmailRef.current = nextEmail;
        } catch (e) {
          // A session lapse can't be fixed by editing the email — surface the
          // recovery escape alongside the inline error (OMG-2111; the escape
          // link is gated on updateFailed, which this catch never set before).
          if (isUpdateEmailSessionFailure(e)) {
            setUpdateFailed(true);
          }
          setErrors({ email: { message: emailErrorMessage(e) } });
          return;
        }
      }

      // Swap the throwaway dummy for the user's real password.
      if (!passwordSetRef.current) {
        await updatePassword({
          oldPassword: dummyPassword,
          newPassword: data.newPassword,
        });
        passwordSetRef.current = true;
      }

      await onComplete();
    } catch (e) {
      // The password swap: Cognito rejected the new password
      // (policy) or the session lapsed. Keep the paid user on a recoverable
      // screen and surface a forgot-password escape — a lapsed session cannot be
      // fixed by retrying here.
      setUpdateFailed(true);
      setErrors({
        newPassword: {
          message: t("setPassword.invalidPassword", {
            defaultValue:
              "We couldn't set that password. Please choose a stronger one.",
          }),
        },
      });
      // A password-policy rejection is routine user input, not a system fault —
      // and the client-side minLength below already catches the common case
      // before it reaches Cognito. Log it at warning level so genuine failures
      // (session lapse, etc.) stay visible as errors instead of being buried in
      // weak-password noise (OMG-2070).
      if (
        e instanceof Error &&
        e.message === AuthenticationErrors.INVALID_PASSWORD
      ) {
        captureMessage(
          "variant set-password: password rejected by policy",
          "warning",
        );
      } else {
        captureException(
          e instanceof Error
            ? e
            : new Error(`variant set-password failed: ${JSON.stringify(e)}`),
          { tags: { context: "variant-set-password" } },
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="w-full max-w-[500px] mx-auto py-[40px] px-[20px]">
      {/* Figma "Below purchase" screen 4 heading treatment: centered,
          bold 18px, ui-pepper — matching the checkout section headings
          ("Choose a way to pay"). */}
      <h1 className="w-full text-center font-mabry-pro-bold text-[18px] leading-[26px] text-ui-pepper">
        {t("setPassword.createAccountTitle", {
          defaultValue: "Create an account",
        })}
      </h1>
      <p className="mt-[8px] mb-[24px] font-mabry-pro-regular text-16px text-ui-coal text-center">
        {t("setPassword.description", {
          defaultValue:
            "Your purchase is complete. Confirm your email and set a password to finish setting up your account.",
        })}
      </p>

      <Form onSubmit={handleSubmit(onSubmit, setErrors)}>
        {/* Editable, pre-filled email (U5/U6): confirm the charge email or
            correct a typo. A correction re-points sign-in + recovery (the
            payment/receipt email is unaffected). Masked from session recording
            (Sentry/Clarity). */}
        <FormInput
          register={() =>
            register("email", {
              ...emailValidationRules(t("js.checkout.emailInvalid")),
              required: t("setPassword.emailRequired", {
                defaultValue: "Enter your email address.",
              }),
              // Re-run the confirm field's mismatch validator when the email
              // changes — without this, editing the email after confirm is
              // filled silently disables the CTA with no visible error.
              deps: ["confirmEmail"],
            })
          }
          className="mb-4 w-full"
          inputClassName={CHECKOUT_INPUT_CLASS}
          label={t("setPassword.emailAddressLabel", {
            defaultValue: "Email Address",
          })}
          error={fieldError("email")}
          errorClassName="font-mabry-pro-medium !px-0"
          inputErrorClassName="!border !border-solid !border-error focus:!border-error"
          type="email"
          autoComplete="email"
          inputMode="email"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          data-sentry-mask
          data-clarity-mask
          onChange={() => setErrors({})}
        />
        <FormInput
          register={() =>
            register("confirmEmail", {
              required: t("setPassword.confirmEmailRequired", {
                defaultValue: "Confirm your email address.",
              }),
              deps: ["email"],
              validate: (confirmEmail) =>
                normalize(getValues("email")) === normalize(confirmEmail ?? "")
                  ? true
                  : t("js.checkout.emailsDontMatch", {
                      defaultValue: "Email addresses don't match.",
                    }),
            })
          }
          className="mb-4 w-full"
          inputClassName={CHECKOUT_INPUT_CLASS}
          label={t("setPassword.confirmEmailAddressLabel", {
            defaultValue: "Confirm Email Address",
          })}
          error={fieldError("confirmEmail")}
          errorClassName="font-mabry-pro-medium !px-0"
          inputErrorClassName="!border !border-solid !border-error focus:!border-error"
          type="email"
          autoComplete="email"
          inputMode="email"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          data-sentry-mask
          data-clarity-mask
          onChange={() => setErrors({})}
        />

        <FormInput
          register={() =>
            register("newPassword", {
              required: t("setPassword.passwordRequired", {
                defaultValue: "Create a password.",
              }),
              // Mirrors the Cognito user-pool policy (min 6, no complexity) and
              // the existing RedeemForm rule, so a too-short password gets inline
              // feedback here instead of a Cognito round-trip + Sentry event.
              minLength: {
                value: 6,
                message: t("setPassword.minLength", {
                  defaultValue: "Password must be at least 6 characters.",
                }),
              },
            })
          }
          className="mb-4 w-full"
          inputClassName={CHECKOUT_INPUT_CLASS}
          label={t("setPassword.passwordLabel", {
            defaultValue: "Password (at least 6 characters)",
          })}
          placeholder={t("setPassword.passwordPlaceholder", {
            defaultValue: "Create password",
          })}
          error={fieldError("newPassword")}
          errorClassName="font-mabry-pro-medium !px-0"
          inputErrorClassName="!border !border-solid !border-error focus:!border-error"
          type="password"
          autoComplete="new-password"
          onChange={() => setErrors({})}
        />
        {/* No Confirm Password field: Jacob's mock keeps Confirm Email only
            (decision: Jason 7/9, OMG-2100). Password managers + the reveal
            affordance cover typo risk; submit reads only email/newPassword. */}
        <FormSubmit
          disabled={isSubmitting || !formState.isValid}
          value={t("setPassword.createAccount", {
            defaultValue: "Create account",
          })}
        />
      </Form>

      {updateFailed && (
        <p className="mt-[16px] text-14px text-ui-coal">
          <Link
            className="text-cta hover:text-cta-hover underline"
            to="/forgot-password"
            state={{ email: accountEmailRef.current }}
          >
            {t("setPassword.recoverInstead", {
              defaultValue:
                "Still having trouble? Reset your password instead.",
            })}
          </Link>
        </p>
      )}
    </div>
  );
};
